import { useId, useRef, useState } from "react";
import { Modal } from "@/components/modal";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import type { Read } from "@/lib/event-form";
import { readPart, type PartRead, type RecordablePart } from "@/lib/results";
import { PartResultFields, type TeamStart } from "@/components/result-fields";
import { ErrorNote, ProblemProvider, useFormProblem } from "@/components/form";

/**
 * The sheets a result is recorded through while recording: one part at a
 * time, each kept on the page when saved until the bar at the bottom sends
 * the workout (decided with Tiago 13 October), and the question a first
 * result asks while it would be private and can be shared.
 */

/**
 * One part's dialog: its boxes, level, Rx+, feel and note — or, on a part
 * done in teams, a team to start — read when Save is pressed and handed back
 * to the page, which keeps it until the workout is sent. A part with a result
 * already, saved or typed, offers Remove.
 */
export function PartDialog({
  part,
  index,
  single,
  team,
  title,
  removable,
  onSave,
  onRemove,
  onClose,
}: {
  /** The part, its `current` what the boxes start from: the saved result, or the one typed before. */
  part: RecordablePart;
  index: number;
  single: boolean;
  team?: TeamStart;
  title: string;
  removable: boolean;
  onSave: (read: PartRead) => void;
  onRemove: () => void;
  onClose: () => void;
}) {
  const formId = useId();
  const form = useRef<HTMLFormElement>(null);
  // What was refused, until the next Save: the box it names is outlined.
  const [refused, setRefused] = useState<Read<unknown> | null>(null);
  const shown = useFormProblem(refused, form);
  return (
    <Modal title={title} onClose={onClose}>
      <p className="text-sm text-muted-foreground">{part.summary}</p>
      <form
        ref={form}
        id={formId}
        className="space-y-4"
        noValidate
        onChange={shown.onChange}
        onSubmit={(event) => {
          event.preventDefault();
          // The event's one level is asked as recording starts: the
          // badge is read as ticked, and cleared as it is sent if not Rx.
          const read = readPart(new FormData(event.currentTarget), part, index, { single, level: null });
          if (read.error !== undefined) {
            setRefused(read);
            return;
          }
          onSave(read.value);
        }}
      >
        <ProblemProvider value={shown.problem}>
          <PartResultFields part={part} index={index} single={single} team={team} form={formId} />
        </ProblemProvider>
        {shown.general && <ErrorNote message={shown.general} />}
        <div className="flex flex-wrap items-center justify-between gap-2">
          {/* Outlined, not red: it only marks the part to go, and Undo brings it back until the workout is sent. */}
          {removable ? (
            <Button type="button" variant="outline" size="lg" onClick={onRemove}>
              Remove
            </Button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <Button type="button" variant="outline" size="lg" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" size="lg">
              Save
            </Button>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">Saving keeps it on this page; nothing is sent until the bar at the bottom sends it.</p>
      </form>
    </Modal>
  );
}

/**
 * Asked as a private result is sent: whether to show it on the space's
 * leaderboard, and with it the notes, feels and photo — one switch for all
 * of them, on every part.
 */
export function ShareStep({
  spaceName,
  busy,
  onAnswer,
  onClose,
}: {
  spaceName: string;
  busy: boolean;
  onAnswer: (answer: { share: boolean; detailsShared: boolean }) => void;
  onClose: () => void;
}) {
  // Unticked every time it is asked: what goes with a result is chosen as it
  // is shared, and unsharing takes it all off.
  const [details, setDetails] = useState(false);
  const detailsId = useId();
  return (
    <Modal title={`Show it on ${spaceName}'s leaderboard?`} onClose={onClose}>
      <p className="text-sm text-muted-foreground">
        Kept private, only you see it, in your log. Shared, it goes on each part's board — and you can take it off
        again whenever you like.
      </p>
      {/* No form: the answer is read from here, and sent with the workout. */}
      <div className="flex items-center gap-3">
        <Checkbox id={detailsId} checked={details} onCheckedChange={(on) => setDetails(on === true)} />
        <Label htmlFor={detailsId} className="font-normal">
          Include notes, feel and photo
        </Label>
      </div>
      <div className="flex justify-end gap-2">
        <Button
          type="button"
          variant="outline"
          size="lg"
          disabled={busy}
          onClick={() => onAnswer({ share: false, detailsShared: details })}
        >
          Keep private
        </Button>
        <Button type="button" size="lg" disabled={busy} onClick={() => onAnswer({ share: true, detailsShared: details })}>
          Share
        </Button>
      </div>
    </Modal>
  );
}
